import { buildReceiptPdfSummary } from './receiptPdfSummary';

import { getContactDocumentName } from '../../utils/contactDisplayName';
import { buildReceiptItemDescription, buildReceiptItemQuantity } from './receiptItemDisplay';

const toNumber = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

const formatThaiDate = (value, dayjs) => {
    const date = dayjs(value);
    if (!date.isValid()) return '-';

    const thaiMonths = [
        'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
        'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'
    ];

    return `${date.date()} ${thaiMonths[date.month()]} ${date.year() + 543}`;
};

const formatShortThaiDate = (value, dayjs) => {
    const date = dayjs(value);
    if (!date.isValid()) return '-';
    return `${date.format('DD/MM')}/${date.year() + 543}`;
};

const paymentMethodLabel = (paymentMethod) => {
    const value = String(paymentMethod || '').trim().toLowerCase();
    if (['cash', 'เงินสด'].includes(value)) return 'cash';
    if (['transfer', 'bank_transfer', 'โอน', 'โอนเงิน', 'เงินโอน', 'โอนผ่านธนาคาร'].includes(value)) return 'transfer';
    if (['cheque', 'check', 'เช็ค'].includes(value)) return 'cheque';
    if (['credit', 'credit_card', 'debit_card', 'บัตรเครดิต', 'บัตรเดบิต'].includes(value)) return 'card';
    return value;
};

const checkbox = (checked, label) => ({
    text: `${checked ? '☑' : '☐'} ${label}`,
    margin: [0, 0, 8, 1]
});

const lineLayout = {
    hLineWidth: () => 0.75,
    vLineWidth: () => 0.75,
    hLineColor: () => '#222222',
    vLineColor: () => '#222222',
    paddingLeft: () => 5,
    paddingRight: () => 5,
    paddingTop: () => 3,
    paddingBottom: () => 3
};

export const createDocDefinitionA4Reference = ({
    documentType,
    documentNumber,
    company,
    receipt,
    logoBase64,
    fixedSignatureLogo,
    fixedStampLogo,
    signatureLayout = { x: 0, y: 0, width: 100 },
    printOptions = {},
    totalAmount,
    paidAmount,
    numberToThaiText,
    formatCurrency,
    dayjs
}) => {
    const summary = buildReceiptPdfSummary(receipt);
    const grandTotal = summary.grandTotal || toNumber(totalAmount);
    const signaturePosition = {
        x: toNumber(signatureLayout.x),
        y: toNumber(signatureLayout.y),
        width: Math.min(Math.max(toNumber(signatureLayout.width) || 100, 40), 180)
    };
    const items = Array.isArray(receipt.items) ? receipt.items : [];
    const itemRows = items.map((item, index) => {
        const quantity = toNumber(item.quantity);
        const unitPrice = toNumber(item.unit_price);
        const isFree = item?.is_free === true || Number(item?.is_free) === 1;
        const amount = isFree ? 0 : (toNumber(item.total_price) || quantity * unitPrice);
        const itemDiscount = toNumber(item.discount_amount ?? item.discount);

        return [
            { text: String(index + 1), alignment: 'center' },
            { text: buildReceiptItemDescription(item), alignment: 'left' },
            { text: buildReceiptItemQuantity(item), alignment: 'center' },
            { text: isFree ? 'แถมฟรี' : formatCurrency(unitPrice), alignment: 'right' },
            { text: formatCurrency(itemDiscount), alignment: 'right' },
            { text: formatCurrency(amount), alignment: 'right' }
        ];
    });
    const fillerHeight = Math.max(45, 238 - (items.length * 25));
    const receiptTitle = receipt.status === 'paid'
        ? 'ใบเสร็จรับเงิน'
        : (documentType || 'ใบแจ้งหนี้').replace(/\s*\([^)]*\)\s*/g, '');
    const customerName = getContactDocumentName(receipt);
    const contactName = receipt.contact_name && receipt.contact_name !== customerName
        ? receipt.contact_name
        : '-';
    const reference = receipt.reference_no || receipt.reference || receipt.quote_number || '-';
    const salesPerson = printOptions.salesperson_name
        || receipt.salesperson_name
        || receipt.sale_name
        || receipt.created_by_name
        || '-';
    const collectorName = printOptions.collector_name
        || company.signature_name
        || receipt.created_by_name
        || 'ผู้รับเงิน';
    const paid = Math.max(0, toNumber(paidAmount));
    const paymentHistory = Array.isArray(receipt.payments) && receipt.payments.length
        ? receipt.payments
        : (paid > 0 ? [{
            payment_date: receipt.payment_date || receipt.receipt_date,
            amount: paid,
            payment_method: receipt.payment_method,
            reference_no: receipt.reference_no,
            bank_name: receipt.bank_name,
            bank_account_no: receipt.bank_account_no,
            source: 'receipt_summary'
        }] : []);
    const paymentMethods = new Set(paymentHistory.map(payment => paymentMethodLabel(payment.payment_method)));
    const latestPayment = paymentHistory[paymentHistory.length - 1] || {};
    const visiblePayments = paymentHistory.slice(-3);

    return {
        pageSize: 'A4',
        pageOrientation: 'portrait',
        pageMargins: [20, 18, 20, 18],
        defaultStyle: {
            font: 'THSarabunNew',
            fontSize: 11,
            lineHeight: 0.9,
            color: '#111111'
        },
        styles: {
            companyName: { fontSize: 16, bold: true },
            documentTitle: { fontSize: 15, bold: true, alignment: 'center' },
            bilingualLabel: { fontSize: 9, alignment: 'center', bold: true },
            amount: { alignment: 'right' },
            strong: { bold: true }
        },
        content: [
            {
                columns: [
                    {
                        width: '68%',
                        columns: [
                            {
                                width: 90,
                                stack: logoBase64
                                    ? [{ image: logoBase64, width: 70, alignment: 'left' }]
                                    : [{ text: '' }],
                                margin: [2, 2, 0, 0]
                            },
                            {
                                width: '*',
                                stack: [
                                    { text: company.company_name || company.name || 'ชื่อบริษัท', style: 'companyName' },
                                    { text: company.address || '-', margin: [0, 2, 0, 0] },
                                    { text: `โทร: ${company.phone || '-'}` },
                                    { text: `เลขประจำตัวผู้เสียภาษี ${company.tax_id || '-'}` }
                                ],
                                margin: [5, 0, 0, 0]
                            }
                        ]
                    },
                    {
                        width: '32%',
                        stack: [
                            {
                                stack: [
                                    {
                                        canvas: [{
                                            type: 'rect',
                                            x: 0,
                                            y: 0,
                                            w: 130,
                                            h: 34,
                                            r: 20,
                                            lineWidth: 0.75,
                                            lineColor: '#222222'
                                        }]
                                    },
                                    {
                                        columns: [
                                            {
                                                width: 130,
                                                text: 'ต้นฉบับ\nORIGINAL',
                                                bold: true,
                                                fontSize: 12,
                                                lineHeight: 0.8,
                                                alignment: 'center'
                                            },
                                            { width: '*', text: '' }
                                        ],
                                        relativePosition: { x: 0, y: -27 },
                                        margin: [0, 0, 0, -17]
                                    }
                                ],
                                margin: [25, 0, 0, 8]
                            },
                            {
                                table: {
                                    widths: ['*'],
                                    body: [[{ text: receiptTitle, style: 'documentTitle', margin: [0, 4, 0, 4] }]]
                                },
                                layout: lineLayout
                            }
                        ]
                    }
                ],
                margin: [0, 0, 0, 6]
            },
            {
                table: {
                    widths: ['56%', '44%'],
                    body: [[
                        {
                            stack: [
                                { text: 'ลูกค้า :', bold: true },
                                { text: 'Customer', fontSize: 9 },
                                { text: customerName, margin: [10, 2, 0, 2] },
                                { text: `เลขประจำตัวผู้เสียภาษี : ${receipt.tax_id || '-'}` },
                                { text: 'ผู้ติดต่อ :', bold: true, margin: [0, 3, 0, 0] },
                                { text: contactName, margin: [10, 0, 0, 0] },
                                { text: `ที่อยู่ : ${receipt.address || '-'}` },
                                { text: `โทรศัพท์ : ${receipt.phone || '-'}` }
                            ],
                            margin: [2, 2, 2, 2]
                        },
                        {
                            stack: [
                                { text: `เลขที่ :     ${documentNumber || '-'}` },
                                { text: 'No.', fontSize: 9, margin: [0, -2, 0, 2] },
                                ...(printOptions.show_document_date !== false
                                    ? [{ text: `วันที่ :      ${formatThaiDate(receipt.receipt_date, dayjs)}` }]
                                    : []),
                                { text: `อ้างอิง :    ${reference}`, margin: [0, 3, 0, 0] },
                                { text: 'Reference', fontSize: 9, margin: [0, -2, 0, 2] },
                                { text: `พนักงานขาย : ${salesPerson}` },
                                { text: 'Salesman', fontSize: 9 }
                            ],
                            margin: [4, 2, 2, 2]
                        }
                    ]]
                },
                layout: lineLayout
            },
            {
                table: {
                    headerRows: 1,
                    dontBreakRows: true,
                    widths: [36, '*', 46, 62, 56, 68],
                    heights: (rowIndex) => {
                        if (rowIndex === 0) return 30;
                        if (rowIndex === itemRows.length + 1) return fillerHeight;
                        return 24;
                    },
                    body: [
                        [
                            { text: 'ลำดับ\nItem', style: 'bilingualLabel', fillColor: '#e5e5e5' },
                            { text: 'รายการสินค้า\nDescription', style: 'bilingualLabel', fillColor: '#e5e5e5' },
                            { text: 'จำนวน\nQuantity', style: 'bilingualLabel', fillColor: '#e5e5e5' },
                            { text: 'ราคา/หน่วย\nUnit price', style: 'bilingualLabel', fillColor: '#e5e5e5' },
                            { text: 'ส่วนลด\nDiscount', style: 'bilingualLabel', fillColor: '#e5e5e5' },
                            { text: 'จำนวนเงิน\nAmount', style: 'bilingualLabel', fillColor: '#e5e5e5' }
                        ],
                        ...itemRows,
                        [{ text: '' }, { text: '' }, { text: '' }, { text: '' }, { text: '' }, { text: '' }]
                    ]
                },
                layout: lineLayout
            },
            {
                table: {
                    widths: ['47%', '22%', '31%'],
                    body: [[
                        {
                            stack: [
                                { text: 'หมายเหตุ :', bold: true },
                                { text: 'Remark', fontSize: 9 },
                                { text: receipt.notes || '-', margin: [8, 4, 0, 0] }
                            ],
                            margin: [2, 1, 2, 1]
                        },
                        {
                            stack: [
                                { text: 'ชำระเงินโดย', bold: true },
                                { text: 'Payment', fontSize: 9 },
                                checkbox(paymentMethods.has('cash'), 'เงินสด'),
                                checkbox(paymentMethods.has('transfer'), 'เงินโอน'),
                                checkbox(paymentMethods.has('cheque'), 'เช็ค'),
                                checkbox(paymentMethods.has('card'), 'บัตร'),
                                { text: 'รายละเอียดรับชำระ', bold: true, margin: [0, 2, 0, 0] },
                                ...visiblePayments.map(payment => ({
                                    text: `${formatShortThaiDate(payment.payment_date, dayjs)} ${payment.payment_method || '-'} ${formatCurrency(payment.amount)}`,
                                    fontSize: 9
                                })),
                                ...(paymentHistory.length > visiblePayments.length
                                    ? [{ text: `และอีก ${paymentHistory.length - visiblePayments.length} รายการ`, fontSize: 9 }]
                                    : []),
                                { text: `ธ. ${latestPayment.bank_name || '-'}`, margin: [0, 2, 0, 0] },
                                { text: `บัญชี ${latestPayment.bank_account_no || '-'}` },
                                { text: `อ้างอิง ${latestPayment.reference_no || '-'}` },
                                { text: `วันที่ ${formatThaiDate(latestPayment.payment_date || receipt.receipt_date, dayjs)}` }
                            ],
                            margin: [2, 1, 2, 1]
                        },
                        {
                            table: {
                                widths: ['56%', '44%'],
                                body: [
                                    [{ text: 'รวม\nTotal', alignment: 'center' }, { text: formatCurrency(summary.grossAmount), style: 'amount' }],
                                    [{ text: 'ส่วนลด\nDiscount', alignment: 'center' }, { text: formatCurrency(summary.discountAmount), style: 'amount' }],
                                    [{ text: 'ราคาก่อนภาษี\nValue Amount', alignment: 'center' }, { text: formatCurrency(summary.netAmount), style: 'amount' }],
                                    [{ text: 'ชำระแล้ว\nPaid', alignment: 'center' }, { text: formatCurrency(paid), style: 'amount' }],
                                    [{ text: `ภาษีมูลค่าเพิ่ม\nVAT ${summary.taxRate || 0}%`, alignment: 'center' }, { text: formatCurrency(summary.taxAmount), style: 'amount' }]
                                ]
                            },
                            layout: lineLayout,
                            margin: [-5, -3, -5, -3]
                        }
                    ]]
                },
                layout: lineLayout
            },
            {
                table: {
                    widths: ['68%', '32%'],
                    body: [[
                        { text: `(${numberToThaiText(grandTotal)})`, bold: true, alignment: 'center', fillColor: '#e5e5e5', margin: [0, 4, 0, 4] },
                        {
                            columns: [
                                { text: 'รวมสุทธิ\nNet Amount', bold: true, alignment: 'center' },
                                { text: formatCurrency(grandTotal), bold: true, alignment: 'right', margin: [0, 5, 0, 0] }
                            ],
                            fillColor: '#e5e5e5',
                            margin: [2, 1, 2, 1]
                        }
                    ]]
                },
                layout: lineLayout
            },
            {
                table: {
                    widths: ['*'],
                    heights: [68],
                    body: [[{
                        columns: [
                            { width: '38%', text: '' },
                            {
                                width: fixedStampLogo ? '44%' : '62%',
                                stack: [
                                    { text: 'ลงชื่อ ผู้รับเงิน', alignment: 'center', margin: [0, 2, 0, 0] },
                                    ...(fixedSignatureLogo ? [{
                                        image: fixedSignatureLogo,
                                        width: signaturePosition.width,
                                        alignment: 'center',
                                        relativePosition: { x: signaturePosition.x, y: signaturePosition.y },
                                        margin: [0, 0, 0, -Math.round(signaturePosition.width * 0.34)]
                                    }] : []),
                                    { text: '_________________________________', alignment: 'center', margin: [0, 22, 0, 0] },
                                    { text: collectorName, alignment: 'center' },
                                    { text: 'วันที่ Date ____ / ____ / ______', alignment: 'center', margin: [0, 1, 0, 0] }
                                ]
                            },
                            ...(fixedStampLogo ? [{
                                width: '18%',
                                stack: [
                                    { image: fixedStampLogo, width: 56, alignment: 'center', margin: [0, 4, 0, 0] }
                                ]
                            }] : [])
                        ]
                    }]]
                },
                layout: lineLayout
            }
        ]
    };
};
